import React, { useState, useEffect } from 'react';
import { dataRetentionAPI } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Database, 
  MessageSquare, 
  Bell, 
  FileText,
  Trash2,
  AlertTriangle,
  Calendar,
  Shield,
  Loader2,
  RefreshCw,
  Info
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const RETENTION_OPTIONS = [
  { value: 0, label: 'Keep Forever', description: 'Messages will never be automatically deleted' },
  { value: 30, label: '30 Days', description: 'Delete messages older than 30 days' },
  { value: 90, label: '90 Days', description: 'Delete messages older than 90 days' },
  { value: 180, label: '6 Months', description: 'Delete messages older than 6 months' },
  { value: 365, label: '1 Year', description: 'Delete messages older than 1 year' },
  { value: 730, label: '2 Years', description: 'Delete messages older than 2 years' },
];

const NOTIFICATION_RETENTION_OPTIONS = [
  { value: 7, label: '7 Days' },
  { value: 30, label: '30 Days' },
  { value: 90, label: '90 Days' },
  { value: 180, label: '6 Months' },
  { value: 365, label: '1 Year' },
];

const AUDIT_LOG_RETENTION_OPTIONS = [
  { value: 30, label: '30 Days' },
  { value: 90, label: '90 Days' },
  { value: 180, label: '6 Months' },
  { value: 365, label: '1 Year' },
  { value: 730, label: '2 Years' },
  { value: 1095, label: '3 Years' },
];

export default function DataRetentionSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({
    chat_retention_days: 0,
    notification_retention_days: 90,
    audit_log_retention_days: 365,
  });
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);

  const isOwner = user?.role === 'owner';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, statsRes] = await Promise.all([
        api.get('/firm/settings'),
        dataRetentionAPI.getStats().catch(() => ({ data: null }))
      ]);
      
      setSettings({
        chat_retention_days: settingsRes.data.chat_retention_days || 0,
        notification_retention_days: settingsRes.data.notification_retention_days || 90,
        audit_log_retention_days: settingsRes.data.audit_log_retention_days || 365,
      });
      
      if (statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/firm/settings', settings);
      toast.success('Data retention settings updated');
      fetchData();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCleanup = async () => {
    setCleaningUp(true);
    try {
      const res = await dataRetentionAPI.runCleanup();
      toast.success(
        `Cleanup complete: ${res.data.chat_messages_deleted} messages, ${res.data.notifications_deleted} notifications deleted`
      );
      setShowCleanupDialog(false);
      fetchData();
    } catch (error) {
      console.error('Failed to run cleanup:', error);
      toast.error(error.response?.data?.detail || 'Failed to run cleanup');
    } finally {
      setCleaningUp(false);
    }
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl" data-testid="data-retention-settings-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Data Retention Policies</h1>
        <p className="text-gray-500 mt-1">
          Configure how long data is stored before automatic cleanup
        </p>
      </div>

      {!isOwner && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
          <Shield className="h-5 w-5 text-amber-600" />
          <div>
            <p className="font-medium text-amber-800">View Only</p>
            <p className="text-sm text-amber-600">
              Only firm owners can modify data retention settings
            </p>
          </div>
        </div>
      )}

      {/* Current Stats Card */}
      {stats && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Current Data Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="h-4 w-4 text-blue-500" />
                  <span className="font-medium text-sm">Chat Messages</span>
                </div>
                <p className="text-2xl font-bold">{formatNumber(stats.chat?.total_messages)}</p>
                <p className="text-xs text-gray-500">
                  Oldest: {formatDate(stats.chat?.oldest_message_date)}
                </p>
                {stats.chat?.messages_to_delete > 0 && (
                  <Badge variant="outline" className="mt-2 text-amber-700 border-amber-300">
                    {formatNumber(stats.chat.messages_to_delete)} to delete
                  </Badge>
                )}
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Bell className="h-4 w-4 text-purple-500" />
                  <span className="font-medium text-sm">Notifications</span>
                </div>
                <p className="text-2xl font-bold">{formatNumber(stats.notifications?.total)}</p>
                <p className="text-xs text-gray-500">
                  Retention: {stats.notifications?.retention_days} days
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-green-500" />
                  <span className="font-medium text-sm">Audit Logs</span>
                </div>
                <p className="text-2xl font-bold">-</p>
                <p className="text-xs text-gray-500">
                  Retention: {stats.audit_logs?.retention_days} days
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Chat Retention Settings */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Chat Message Retention
          </CardTitle>
          <CardDescription>
            Configure how long team chat messages are stored
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Retention Period</Label>
              <Select
                value={settings.chat_retention_days.toString()}
                onValueChange={(value) => setSettings(prev => ({ 
                  ...prev, 
                  chat_retention_days: parseInt(value) 
                }))}
                disabled={!isOwner}
              >
                <SelectTrigger className="w-full md:w-[300px]" data-testid="chat-retention-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RETENTION_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500 mt-2">
                {RETENTION_OPTIONS.find(o => o.value === settings.chat_retention_days)?.description}
              </p>
            </div>

            {settings.chat_retention_days > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Warning</p>
                  <p className="text-amber-700">
                    Messages older than {settings.chat_retention_days} days will be permanently deleted 
                    during cleanup. This action cannot be undone.
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Notification Retention Settings */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Retention
          </CardTitle>
          <CardDescription>
            Configure how long notifications are stored
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div>
            <Label className="mb-2 block">Retention Period</Label>
            <Select
              value={settings.notification_retention_days.toString()}
              onValueChange={(value) => setSettings(prev => ({ 
                ...prev, 
                notification_retention_days: parseInt(value) 
              }))}
              disabled={!isOwner}
            >
              <SelectTrigger className="w-full md:w-[300px]" data-testid="notification-retention-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NOTIFICATION_RETENTION_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log Retention Settings */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Audit Log Retention
          </CardTitle>
          <CardDescription>
            Configure how long audit logs are stored (minimum 30 days for compliance)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div>
            <Label className="mb-2 block">Retention Period</Label>
            <Select
              value={settings.audit_log_retention_days.toString()}
              onValueChange={(value) => setSettings(prev => ({ 
                ...prev, 
                audit_log_retention_days: parseInt(value) 
              }))}
              disabled={!isOwner}
            >
              <SelectTrigger className="w-full md:w-[300px]" data-testid="audit-retention-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUDIT_LOG_RETENTION_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      {isOwner && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setShowCleanupDialog(true)}
            className="gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Run Cleanup Now
          </Button>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={fetchData} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-retention-btn">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Info Card */}
      <Card className="mt-6">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <p className="font-medium">About Data Retention</p>
              <p className="text-sm text-gray-500 mt-1">
                Data retention policies help manage storage and comply with privacy regulations. 
                Deleted data cannot be recovered. Changes take effect during the next cleanup run.
                For legal compliance, we recommend keeping audit logs for at least 1 year.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cleanup Confirmation Dialog */}
      <Dialog open={showCleanupDialog} onOpenChange={setShowCleanupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Run Data Cleanup
            </DialogTitle>
            <DialogDescription>
              This will permanently delete data older than your retention periods.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
              <p><strong>Chat Messages:</strong> {settings.chat_retention_days > 0 
                ? `Delete messages older than ${settings.chat_retention_days} days` 
                : 'No messages will be deleted'}</p>
              <p><strong>Notifications:</strong> Delete notifications older than {settings.notification_retention_days} days</p>
            </div>
            
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 font-medium">
                Warning: This action cannot be undone!
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowCleanupDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCleanup}
              disabled={cleaningUp}
              data-testid="confirm-cleanup-btn"
            >
              {cleaningUp ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Run Cleanup
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
